import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { api, TrainingSessionSummary } from '../../services/api';

/**
 * Admin: create / edit a single TransformationTrainingSession.
 *
 * `SessionDate` is constrained to the parent event's [startDate, endDate]
 * window using the native `<input type="date">` `min` / `max` attributes.
 * Start/end times use `<input type="time">` for a consistent picker across
 * browsers.
 */

interface SessionFormDialogProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  eventStartDate: string; // ISO or 'YYYY-MM-DD'
  eventEndDate: string;   // ISO or 'YYYY-MM-DD'
  session: TrainingSessionSummary | null; // null → create mode
  onSaved: (session: TrainingSessionSummary) => void;
}

function toDateOnly(value: string | null | undefined): string {
  if (!value) return '';
  return String(value).slice(0, 10);
}

function toTimeInputValue(value: string | null | undefined): string {
  if (!value) return '';
  // Backend returns "HH:MM:SS"; the <input type="time"> takes "HH:MM".
  const m = /^(\d{1,2}):(\d{2})/.exec(String(value));
  if (!m) return '';
  return `${m[1].padStart(2, '0')}:${m[2]}`;
}

const SessionFormDialog: React.FC<SessionFormDialogProps> = ({
  open,
  onClose,
  eventId,
  eventStartDate,
  eventEndDate,
  session,
  onSaved,
}) => {
  const isEdit = Boolean(session);
  const minDate = useMemo(() => toDateOnly(eventStartDate), [eventStartDate]);
  const maxDate = useMemo(() => toDateOnly(eventEndDate) || minDate, [eventEndDate, minDate]);

  const [name, setName] = useState<string>('');
  const [sessionDate, setSessionDate] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (session) {
      setName(session.name);
      setSessionDate(toDateOnly(session.sessionDate));
      setStartTime(toTimeInputValue(session.startTime));
      setEndTime(toTimeInputValue(session.endTime));
    } else {
      setName('');
      setSessionDate(minDate);
      setStartTime('09:00');
      setEndTime('17:00');
    }
  }, [open, session, minDate]);

  const validate = (): string | null => {
    if (!name.trim()) return 'Name is required';
    if (!sessionDate) return 'Session date is required';
    if (sessionDate < minDate || (maxDate && sessionDate > maxDate)) {
      return `Session date must be between ${minDate} and ${maxDate || minDate}`;
    }
    if (!startTime || !endTime) return 'Start and end time are required';
    if (endTime <= startTime) return 'End time must be after start time';
    return null;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        sessionDate,
        startTime: `${startTime}:00`,
        endTime: `${endTime}:00`,
      };
      const saved = session
        ? await api.updateEventSession(eventId, session.id, payload)
        : await api.createEventSession(eventId, payload);
      onSaved(saved);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save session');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Session' : 'Add Session'}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Session name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            required
            disabled={saving}
          />
          <TextField
            label="Session date"
            type="date"
            value={sessionDate}
            onChange={(e) => setSessionDate(e.target.value)}
            fullWidth
            size="small"
            required
            disabled={saving}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: minDate, max: maxDate || undefined }}
            helperText={
              minDate
                ? `Must be between ${minDate}${maxDate && maxDate !== minDate ? ` and ${maxDate}` : ''}`
                : undefined
            }
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Start time"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              fullWidth
              size="small"
              required
              disabled={saving}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
            />
            <TextField
              label="End time"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              fullWidth
              size="small"
              required
              disabled={saving}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionFormDialog;
